/**
 * `pending-alert-rules-engine` v0 — Zod contracts.
 *
 * The rules table holds operator-authored rule defs; the firings
 * ledger holds one row per dispatched alert (used for the cooldown
 * lookup). Both surfaces are admin-only — no per-org carving at v0.
 */

import { z } from 'zod';

export const ALERT_RULE_KINDS = [
  'error_rate_route',
  'error_rate_global',
  'audit_verb_rate',
  'latency_p95_route',
  'cache_eviction_rate',
] as const;
export const AlertRuleKindSchema = z.enum(ALERT_RULE_KINDS);
export type AlertRuleKind = z.infer<typeof AlertRuleKindSchema>;

export const ALERT_RULE_CHANNELS = ['discord', 'slack', 'email', 'webhook'] as const;
export const AlertRuleChannelSchema = z.enum(ALERT_RULE_CHANNELS);
export type AlertRuleChannel = z.infer<typeof AlertRuleChannelSchema>;

export const ALERT_RULE_SEVERITIES = ['info', 'warn', 'critical'] as const;
export const AlertRuleSeveritySchema = z.enum(ALERT_RULE_SEVERITIES);
export type AlertRuleSeverity = z.infer<typeof AlertRuleSeveritySchema>;

export const AlertRuleMatchSchema = z
  .object({
    route: z.string().min(1).max(256).optional(),
    verb: z.string().min(1).max(128).optional(),
    cacheName: z.string().min(1).max(128).optional(),
  })
  .strict();
export type AlertRuleMatch = z.infer<typeof AlertRuleMatchSchema>;

export const AlertRuleSchema = z.object({
  id: z.string().uuid(),
  kind: AlertRuleKindSchema,
  match: AlertRuleMatchSchema,
  threshold: z.number().nonnegative(),
  windowSec: z.number().int().min(60).max(3600),
  cooldownSec: z.number().int().min(60).max(86400),
  channel: AlertRuleChannelSchema,
  severity: AlertRuleSeveritySchema,
  active: z.boolean(),
  reason: z.string().min(1).max(500),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AlertRule = z.infer<typeof AlertRuleSchema>;

export const CreateAlertRuleRequestSchema = z.object({
  kind: AlertRuleKindSchema,
  match: AlertRuleMatchSchema.default({}),
  threshold: z.number().nonnegative(),
  windowSec: z.number().int().min(60).max(3600).default(300),
  cooldownSec: z.number().int().min(60).max(86400).default(900),
  channel: AlertRuleChannelSchema.default('discord'),
  severity: AlertRuleSeveritySchema.default('warn'),
  reason: z.string().min(1).max(500),
});
export type CreateAlertRuleRequest = z.infer<typeof CreateAlertRuleRequestSchema>;

export const UpdateAlertRuleRequestSchema = z.object({
  threshold: z.number().nonnegative().optional(),
  windowSec: z.number().int().min(60).max(3600).optional(),
  cooldownSec: z.number().int().min(60).max(86400).optional(),
  channel: AlertRuleChannelSchema.optional(),
  severity: AlertRuleSeveritySchema.optional(),
  active: z.boolean().optional(),
  reason: z.string().min(1).max(500).optional(),
});
export type UpdateAlertRuleRequest = z.infer<typeof UpdateAlertRuleRequestSchema>;

export const AlertRuleFiringSchema = z.object({
  id: z.number(),
  ruleId: z.string().uuid(),
  firedAt: z.string(),
  observed: z.number(),
  payload: z.record(z.unknown()),
});
export type AlertRuleFiring = z.infer<typeof AlertRuleFiringSchema>;

export const ListAlertRulesResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  rules: z.array(AlertRuleSchema),
});
export type ListAlertRulesResponse = z.infer<typeof ListAlertRulesResponseSchema>;

export const ListAlertRuleFiringsResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  firings: z.array(AlertRuleFiringSchema),
});
export type ListAlertRuleFiringsResponse = z.infer<typeof ListAlertRuleFiringsResponseSchema>;
